import { useEffect, useRef, useState } from "react";
import { Environment } from "../shared/core/Environment";
import style from "../styles/pages/cdp/cdp.module.scss";

import { Loading } from "../shared/components/Loading";
import { Title } from "../shared/components/Title";
import { If } from "../shared/components/control-statements/If";

import styles from "@styles/pages/cdp/cdp.module.scss";
import { Customers } from "shared/containers/cdp/customers/Customers";
import { Segmentations } from "shared/containers/cdp/segmentations/Segmentations";
import { Empty } from "../shared/components/Empty";
import { CustomerDrawer } from "../shared/containers/oraculo/customers/CustomerDrawer";

import { Button, Drawer } from "@components";
import { useDebouncedCallback } from "@hooks";
import classNames from "classnames/bind";
import OcdpAPI2 from "providers/OcdpAPI2";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import { InputSearch } from "shared/components/InputSearch";
import { Icon } from "shared/components/core/Icon";
import ExportAudience from "shared/containers/cdp/audiences/ExportAudience";
import SaveAudience from "shared/containers/cdp/audiences/SaveAudience";
import OrderingAndLimitation from "shared/containers/cdp/customers/OrderingAndLimitation";
import Filters from "shared/containers/cdp/filters/Filters";
import TargetingGuide from "shared/containers/cdp/targeting-guide/TargetingGuide";
import { ProgressModal } from "shared/containers/oraculo/ProgressModal";
import { Cdp } from "shared/core/cdp";
import { Numbers } from "shared/utils/Numbers";
import { FlowAPI } from "providers/FlowAPI";
import { Oraculo } from "shared/core/oraculo";
import OcdpAPI from "providers/OcdpAPI";
import OracleDrawer from "shared/containers/oraculo/OracleDrawer";
import AudienceFilters from "shared/containers/oraculo/AudienceFilters";
import { Phone } from "shared/utils/Phone";
import { CPF } from "shared/tools/CPF";
import { Variables } from "shared/containers/cdp/filters/Variables";
import { UserStorage } from "shared/tools/UserStorage";
import CustomerMarkupsDrawer from "shared/containers/oraculo/customers/CustomerDrawer/Data/CustomerMarkupsDrawer";
import { GodMode } from "shared/core/GodMode";

const css = classNames.bind(styles);

const tabs = {
  CUSTOMERS: "CUSTOMERS",
  SEGMENTATION: "SEGMENTATION",
};

const ocdp_api = new OcdpAPI2();

export default function FullDatabase({
  onClose,
  selectedTabFullDataBase,
  audience,
  context,
}) {
  const [customers, setCustomers] = useState([]);
  const [selected_customer, setSelectedCustomer] = useState(null);
  const [selectedTab, setSelectedTab] = useState(tabs.CUSTOMERS);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchSegments, setSearchSegments] = useState("");
  const [page_infos, setPageInfos] = useState({
    page: null,
    execution_id: null,
    has_next: false,
    force: false,
    sort: null,
    limit: null,
  });
  const [sort_and_limit, setSortAndLimit] = useState({
    sort: null,
    limit: null,
  });
  const [openFilters, setOpenFilters] = useState(false);
  const [openOrdering, setOpenOrdering] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState({});
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [appliedSegments, setAppliedSegments] = useState({});
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [openExecute, setOpenExecute] = useState(false);
  const [openSave, setOpenSave] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [allSegments, setAllSegments] = useState(null);
  const [loadingSegments, setLoadingSegments] = useState(true);
  const [totals, setTotals] = useState(null);
  const [openClear, setOpenClear] = useState(false);
  const [openAudienceFilters, setOpenAudienceFilters] = useState(false);
  const [openMarkups, setOpenMarkups] = useState(false);
  const [customerAttributeVariables, setCustomerAttributeVariables] = useState([]);

  const initialSegmentsRef = useRef(null);
  const initialTotalsRef = useRef(null);
  const initialCustomersRef = useRef(null);

  useEffect(() => {
    if (context) {
      setCustomers([]);
      const { brand, unit } = context;
      execute({
        brand_id: brand?.id,
        unit_id: unit?.id,
        schema: [],
        sort: null,
        limit: null,
        force_context: true,
      });

      return;
    }

    if (audience) {
      setLoading(true);
      const { brand, unit, schema, execution_id, sort, limit } = audience;

      audience.brand = audience.brand_id ? { id: audience.brand_id } : null;
      audience.unit = audience.unit_id ? { id: audience.unit_id } : null;

      if (audience.type === "STATIC") {
        const infos = {
          execution_id: execution_id,
          has_next: true,
          page: 0,
          force: true,
          sort: sort,
          limit: limit,
        };
        updatePageInfos(infos, true);
      } else {
        execute({
          brand_id: brand?.id,
          unit_id: unit?.id,
          schema: schema || [],
          sort: sort || null,
          limit: limit || null,
          filters: audience?.filters || [],
        });
      }
    } else {
      const configs = Environment.configurations;

      if (configs.ocdp_execution_id_v2) {
        const infos = {
          execution_id: configs.ocdp_execution_id_v2,
          has_next: true,
          page: 0,
          force: true,
        };
        updatePageInfos(infos, true);
      }
    }
  }, [audience]);

  const allSegmentsRef = useRef(null);

  useEffect(() => {
    const { schema } = audience || {};

    if (allSegmentsRef.current) return;

    if (!allSegmentsRef.current) {
      allSegmentsRef.current = allSegments;
    }

    if (audience?.version == "V2") {
      setSelectedSegments(translateQueryIntoSegments(schema || []));

      if (audience.filters) {
        setSelectedFilters(translateQueryIntoFilters(audience?.filters || []));
      }
    }
  }, [audience, allSegments]);

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (selectedTabFullDataBase === "create-audience")
      setSelectedTab(tabs.SEGMENTATION);
  }, [selectedTabFullDataBase]);

  const fetchAllSegments = async (all_segments) => {
    try {
      const fetchFunctions = [
        { key: "source_id", fetcher: fetchSources },
        { key: "conversion_coupon_use", fetcher: fetchCoupons },
        { key: "unit_id", fetcher: fetchUnits },
        { key: "brand_id", fetcher: fetchBrands },
        { key: "order_origin", fetcher: fetchOrigins },
        { key: "order_city", fetcher: fetchCities },
        { key: "order_state", fetcher: fetchStates },
        { key: "order_region", fetcher: fetchRegions },
        { key: "ocdp_tag_orders", fetcher: fetchOrderTags },
        { key: "ocdp_tag_customers", fetcher: fetchCustomerTags },
        { key: "conversion_skus", fetcher: fetchSkus },
        { key: "customer_flow", fetcher: fetchFlows },
      ];

      const results = await Promise.allSettled(
        fetchFunctions.map(({ fetcher }) => fetcher(all_segments)),
      );

      const updatedSegments = results.reduce(
        (acc, result, index) => {
          const { key } = fetchFunctions[index];

          if (result.status === "fulfilled") {
            acc[key] = result.value;
          } else {
            console.error(`Erro ao buscar ${key}:`, result.reason);
            delete acc[key];
          }

          return acc;
        },
        { ...all_segments },
      );

      console.log('updatedSegments', updatedSegments)

      return updatedSegments;
    } catch (error) {
      console.error("Erro ao buscar os dados:", error);
      throw error;
    }
  };

  const loadSegments = async () => {
    try {
      setLoadingSegments(true);
      const response = await fetch("/Segments.json");
      const all_segments = await response.json();

      const customerAttributes = Environment.configurations.customer_attributes || [];

      customerAttributes
        .filter((attr) => attr.tipo === "OPTIONS")
        .forEach((attr) => {
          const key = `customer_attributes_${attr.alias}`;
          all_segments[key] = {
            id: key,
            name: attr.titulo,
            options: (attr.opcoes || []).map((opcao) => ({ id: opcao, name: opcao })),
            modifiers: [{ id: "has_some", name: "Em uma das" }],
          };
        });

      const textVariables = customerAttributes
        .filter((attr) => attr.tipo === "TEXT")
        .map((attr) => ({
          label: attr.titulo,
          type: `customer_attributes_${attr.alias}`,
        }));
      setCustomerAttributeVariables(textVariables);

      const updatedSegments = await fetchAllSegments(all_segments);
      setAllSegments(updatedSegments);
      fetchStats(updatedSegments);
    } catch (error) {
      console.error("Erro ao carregar os segmentos:", error);
      setLoadingSegments(false);
    }
  };

  useEffect(() => {
    if (
      (page_infos.execution_id || page_infos.ocdp_execution_id_v2) &&
      page_infos.force &&
      !allSegments
    )
      loadSegments();
  }, [page_infos, allSegments]);

  const brandId = () => context?.brand?.id || null;
  const unitId = () => context?.unit?.id || null;

  const updatePageInfos = (infos, ignore_previous = false) => {
    const defaults = {
      page: 0,
      execution_id: null,
      has_next: false,
      force: false,
      sort: null,
      limit: null,
    };

    setPageInfos((old) => {
      if (ignore_previous) {
        return Object.assign(defaults, infos);
      }

      const _old = Object.assign(defaults, old);
      return Object.assign(_old, infos);
    });
  };

  const isPhoneNumber = (string) => {
    const normalizedInput = string.trim();
    const phoneRegex = /^(?:\(\d{2}\)\s?|\d{2}\s?)?\d{4,5}-?\d{4}$/;

    return phoneRegex.test(normalizedInput);
  };

  const fetchData = async (loadMore = false) => {
    if (page_infos.execution_id == null) {
      setCustomers([]);
      updatePageInfos({}, true);
      setTotals(null);
      return;
    }

    const { page, execution_id } = page_infos;

    // if (!page_infos.force) return;

    const params = {
      brand_id: brandId(),
      unit_id: unitId(),
      page: page,
      size: 201,
      version: audience?.version,
    };

    params.term = search;
    if (isPhoneNumber(search) || (/^\d+$/.test(search) && search.length > 7)) {
      params.term = Numbers.onlyDigits(search);
    }

    let customers_page = {};

    if (audience?.version == "V1") {
      const ocdp_api = new OcdpAPI();
      customers_page = await ocdp_api.fetch(execution_id, { params });
    } else customers_page = await ocdp_api.fetch(execution_id, params);

    const { page_items, page_size, page_number } = customers_page;

    if (loadMore) {
      setCustomers([...customers, ...page_items]);
    } else {
      setCustomers([...page_items]);

      if (initialCustomersRef.current === null) {
        initialCustomersRef.current = [...page_items];
      }

      if (audience?.version == "V1") {
        const ocdp_api = new OcdpAPI();
        const response = await ocdp_api.stats(execution_id, {
          brand_id: brandId(),
          unit_id: unitId(),
          filters: {},
        });
        const { stats } = response;
        setTotals(stats?.customers || 0);

        if (initialTotalsRef.current === null) {
          initialTotalsRef.current = stats?.customers || 0;
        }
      } else
        ocdp_api.totals(page_infos.execution_id, params).then((t) => {
          setTotals(t);

          if (initialTotalsRef.current === null) {
            initialTotalsRef.current = t;
          }
        });
    }

    const infos = {
      execution_id: execution_id,
      has_next: page_items.length == page_size,
      page: +page_number + 1,
      force: false,
    };
    updatePageInfos(infos);

    setAppliedSegments(selectedSegments);
    setAppliedFilters(selectedFilters);
    // setSelectedTab(tabs.CUSTOMERS);
  };

  useEffect(() => {
    if (page_infos.force) {
      const proms = [fetchData()];
      Promise.all(proms).then(() => {
        setOpenExecute(false);
        setLoading(false);
      });
      loadSegments();
    }
  }, [page_infos]);

  const execute = async ({
    brand_id,
    unit_id,
    schema,
    filters,
    sort,
    limit,
    force_context,
  }) => {
    brand_id = force_context ? brand_id || null : brand_id || brandId();
    unit_id = force_context ? unit_id || null : unit_id || unitId();

    setSelectedTab(tabs.CUSTOMERS);

    const { execution_id, status } =
      (await waitQueryExecution(schema, {
        brand_id,
        unit_id,
        filters,
        sort,
        limit,
        version: audience?.version,
      })) || {};

    if (!execution_id || status == "ERROR") {
      return alert({
        text: "Ocorreu um erro durante a execução da consulta",
      });
    }

    handleOnQuery({
      execution_id,
      status,
      sort,
      limit,
    });
  };

  const handleOnQuery = async ({ execution_id, sort = null, limit = null }) => {
    const infos = {
      execution_id: execution_id,
      has_next: true,
      page: 0,
      force: true,
      sort: sort,
      limit: limit,
    };
    updatePageInfos(infos, true);
  };

  useEffect(() => {
    if (!scrollContainerRef.current) return;

    if (scrollContainerRef.current) scrollContainerRef.current.scrollTo(0, 0);
  }, [selectedTab]);

  const mergeSegments = (data, updatedSegments) => {
    const result = {};

    if (!updatedSegments || (updatedSegments || []).length === 0)
      updatedSegments = allSegments;

    for (const key in updatedSegments) {
      if (data?.[key]) {
        result[key] = {
          ...updatedSegments[key],
          options: updatedSegments[key].options.map((option) => ({
            ...option,
            data: data[key][option.id] || [],
          })),
        };
      } else {
        result[key] = {
          ...updatedSegments[key],
          options: updatedSegments[key].options.map((option) => ({
            ...option,
            data: [],
          })),
        };
      }
    }

    return result;
  };

  const fetchStats = async (updatedSegments = [], infos) => {
    const { execution_id } = infos || page_infos;

    const response = await ocdp_api.stats(execution_id, {
      filters: {},
      version: audience?.version,
    });

    const mergedSegments = mergeSegments(response?.stats, updatedSegments);

    // csv_export(mergedSegments);
    setAllSegments(mergedSegments);
    if (initialSegmentsRef.current === null) {
      initialSegmentsRef.current = mergedSegments;
    }

    if (Object.keys(mergedSegments).length > 0) {
      setLoadingSegments(false);
    }
  };

  const csv_export = async (mergedSegments) => {
    const csv = ["Variável;Valor;Quantidade"];
    Object.entries(mergedSegments).forEach(([key, data]) => {
      data.options.forEach((o) => {
        csv.push([data.name, o.name, o.data.has_some].join(";"));
      });
    });

    console.log(csv.join("\n"));
  };

  const fetchSources = async (all_segments) => {
    const sourcesResponse = await OlgaEnvironmentsAPI.getUsedSources();
    const sources = sourcesResponse.getData([]);

    const options = {
      options: sources
        .filter((item) => item.origin && item.name)
        .map((item) => ({
          id: item.origin,
          name: item.name,
        })),
    };

    return {
      ...all_segments["source_id"],
      ...options,
    };
  };

  const fetchUnits = async (all_segments) => {
    const unitsResponse = await OlgaEnvironmentsAPI.getUnits();
    const units = unitsResponse.getData([]);

    const options = {
      options: units
        .filter((item) => item.id && item.name)
        .map((item) => ({
          id: item.id,
          name: item.name,
        })),
    };

    return {
      ...all_segments["unit_id"],
      ...options,
    };
  };

  const fetchBrands = async (all_segments) => {
    const brandsResponse = await OlgaEnvironmentsAPI.getBrands();
    const brands = brandsResponse.getData([]);

    const options = {
      options: brands
        .filter((item) => item.id && item.name)
        .map((item) => ({
          id: item.id,
          name: item.name,
        })),
    };

    return {
      ...all_segments["brand_id"],
      ...options,
    };
  };

  const fetchOrigins = async (all_segments) => {
    const originsResponse =
      await OlgaEnvironmentsAPI.getAllAvailableExternalSourcesConfigs();
    const origins = originsResponse.getData([]);

    const options = {
      options: origins
        .filter((item) => item.external_source)
        .map((item) => ({
          id: item.external_source,
          name: item.external_source,
        })),
    };

    return {
      ...all_segments["order_origin"],
      ...options,
    };
  };

  const fetchCoupons = async (all_segments) => {
    const couponsResponse = await OlgaEnvironmentsAPI.getCoupons();
    const coupons = couponsResponse.getData([]);

    const options = {
      options: coupons
        .filter((item) => item.id && item.code)
        .map((item) => ({
          id: item.code,
          name: item.code,
        })),
    };

    return {
      ...all_segments["conversion_coupon_use"],
      ...options,
    };
  };

  const fetchRegions = async (all_segments) => {
    const regionsResponse = await ocdp_api.getRegions();
    const regions = regionsResponse;

    const options = {
      options: regions
        .filter((item) => item)
        .map((item) => ({
          id: item,
          name: item,
        })),
    };

    return {
      ...all_segments["order_region"],
      ...options,
    };
  };

  const fetchOrderTags = async (all_segments) => {
    const tagsResponse = await ocdp_api.getMarkups();
    const tags = tagsResponse;

    const filteredTags = tags.filter((item) => item.type === "ORDER");

    const options = {
      options: filteredTags
        .filter((item) => item)
        .map((item) => ({
          id: item?.id,
          name: item?.name,
        })),
    };

    return {
      ...all_segments["ocdp_tag_orders"],
      ...options,
    };
  };

  const fetchCustomerTags = async (all_segments) => {
    const tagsResponse = await ocdp_api.getMarkups();
    const tags = tagsResponse;

    const filteredTags = tags.filter((item) => item.type === "CUSTOMER");

    const options = {
      options: filteredTags
        .filter((item) => item)
        .map((item) => ({
          id: item?.id,
          name: item?.name,
        })),
    };

    return {
      ...all_segments["ocdp_tag_customers"],
      ...options,
    };
  };

  const fetchSkus = async (all_segments) => {
    const skusResponse = await ocdp_api.getSkus();
    const skus = skusResponse;

    const options = {
      options: skus
        .filter((item) => item.id && item.name)
        .map((item) => ({
          id: item.id,
          name: item.name,
        })),
    };

    return {
      ...all_segments["conversion_skus"],
      ...options,
    };
  };

  const fetchCities = async (all_segments) => {
    const citiesResponse = await ocdp_api.getCities();
    const cities = citiesResponse;

    const options = {
      options: cities
        .filter((item) => item)
        .map((item) => ({
          id: item,
          name: item,
        })),
    };

    return {
      ...all_segments["order_city"],
      ...options,
    };
  };

  const fetchStates = async (all_segments) => {
    const stateResponse = await ocdp_api.getStates();
    const states = stateResponse;

    const options = {
      options: states
        .filter((item) => item)
        .map((item) => ({
          id: item,
          name: item,
        })),
    };

    return {
      ...all_segments["order_state"],
      ...options,
    };
  };

  const fetchFlows = async (all_segments) => {
    const folderFilter = 'ALL';
    const myFlowsResponse = await FlowAPI.getFlows(folderFilter);
    const flows = myFlowsResponse.getData([]);

    const myArchivedFlowsResponse = await FlowAPI.getArchivedFlows();
    const archived_flows = myArchivedFlowsResponse.getData([]);

    const options = {
      options: [...flows, ...archived_flows]
        .filter((item) => item)
        .map((item) => ({
          id: item.id,
          name: item.name,
        })),
    };

    return {
      ...all_segments["customer_flow"],
      ...options,
    };
  };

  const translateQueryIntoSegments = (schema) => {
    const segments = {};

    schema.forEach((item) => {
      const { key, values, modifier } = item;
      segments[key] = {
        name: allSegments?.[key]?.name || key,
        options: values.map((value) => ({
          id: value,
          name:
            allSegments?.[key]?.options.find((opt) => opt.id === value)?.name ||
            value,
        })),
        modifier: modifier ? { id: modifier } : null,
      };
    });

    return segments;
  };

  const transformSegmentsIntoQuery = () => {
    const transformed = Object.entries(selectedSegments).map(
      ([key, value]) => ({
        key,
        values: value.options.map((option) => option.id),
        modifier: value.modifier?.id || null,
      }),
    );

    return transformed || [];
  };

  const translateQueryIntoFilters = (filters) => {
    return filters.map((item) => {
      const { alias, operand, values, value_type } = item;
      const staticVar = Variables.find((v) => v.type === alias);
      const dynamicVar = customerAttributeVariables.find((v) => v.type === alias);
      const transformedItem = {
        active: true,
        name: staticVar?.label || dynamicVar?.label || alias,
        type: alias,
        operand: operand,
        values: values.length > 1 ? values : values[0],
        value_type: value_type || "string",
      };

      if (alias === "c.phone") {
        transformedItem.values = values.map((value) => Phone.format(value));
      }

      if (alias === "c.doc") {
        transformedItem.values = values.map((value) => CPF.mask(value));
      }

      return transformedItem;
    });
  };

  const transformFiltersIntoQuery = () => {
    const filters = selectedFilters.map((filter) => {
      const alias = filter.type;
      const values = filter.values;
      const operand = filter.operand;
      const value_type = filter.value_type;
      const transformedItem = { alias, operand, values, value_type };

      if (alias === "c.phone") {
        transformedItem.values = values.map((value) =>
          value.replace(/[\s()-]/g, ""),
        );
      }

      if (alias === "c.doc") {
        transformedItem.values = values.map((value) =>
          value.replace(/[^\d]/g, ""),
        );
      }

      return transformedItem;
    });

    // selectedFilters.forEach((item) => {
    //   const key = item.type;
    //   const transformedItem = {
    //     operand: item.operand,
    //     values: item.values,
    //   };

    //   if (item.range) transformedItem.range = item.range;
    //   if (item.value_type) transformedItem.value_type = item.value_type;

    //   if (key === "c.phone") {
    //     transformedItem.values = item.values.map((value) =>
    //       value.replace(/[\s()-]/g, "")
    //     );
    //   }
    //   if (key === "c.doc") {
    //     transformedItem.values = item.values.map((value) =>
    //       value.replace(/[^\d]/g, "")
    //     );
    //   }

    //   if (filters[key]) {
    //     if (!Array.isArray(filters[key])) filters[key] = [filters[key]];

    //     filters[key].push(transformedItem);
    //   } else filters[key] = transformedItem;
    // });

    return filters;
  };

  const debouncedSearch = useDebouncedCallback((nextValue) => {
    setCustomers([]);

    if (OcdpAPI2.is_request_running("fetch")) {
      OcdpAPI2.cancel_request("fetch");
      OcdpAPI2.cancel_request("stats");
    }
    setLoading(true);

    const infos = {
      execution_id: page_infos.execution_id,
      page: 0,
      force: true,
      has_next: false,
    };
    updatePageInfos(infos);
  }, 1000);

  const getTotalAppliedSegments = () =>
    Object.values(appliedSegments).reduce((sum, item) => {
      return sum + (item.options ? item.options.length : 0);
    }, 0);

  const hasAppliedSegments = Object.values(appliedSegments)?.length > 0;
  const hasAppliedFilters = Object.values(appliedFilters)?.length > 0;

  return (
    <>
      <Title
        title={audience?.name || "Base completa"}
        onBack={{ callback: () => onClose(), name: "Base de dados" }}
      >
        <div className="d-flex align-center gap-10">

          {GodMode.isGodMode && audience?.id && (
            <div
              className={css({
                totalCustomers: true,
                filtered: audience?.id,
              })}
            >
              Público: {audience.id}
            </div>
          )}


          {!!totals && (
            <div
              className={css({
                totalCustomers: true,
                filtered: hasAppliedSegments || audience?.id,
              })}
            >
              {hasAppliedSegments || audience?.id ? (
                <i className="fal fa-bars-filter" />
              ) : (
                <i className="fal fa-users" />
              )}
              {Numbers.formatInt(totals)} clientes
            </div>
          )}

          <If cond={audience?.version == "V1" && !!totals}>
            <Button onClick={() => setOpenAudienceFilters(true)}>
              Ver filtragem
            </Button>
          </If>

          {(audience?.id || hasAppliedSegments || hasAppliedFilters) && (
            <>
              <Button
                onClick={() => setOpenExport(true)}
                design="default-icon-mini"
              >
                <Icon name="fa-ellipsis-v" />
              </Button>

              {/* The Famous ou Fogo no chão */}
              <If cond={Environment.id === 51 || Environment.id === 108}>
                <Button onClick={() => setOpenMarkups(true)}>
                  Marcar clientes
                </Button>
              </If>

              <If cond={audience?.version !== "V1"}>
                <Button design="primary" onClick={() => setOpenSave(true)}>
                  {!audience ? "Salvar público" : "Salvar edições"}
                </Button>
              </If>
            </>
          )}
        </div>
      </Title>

      {!openExecute && (
        <>
          <div className={styles.fullDatabaseHeader}>
            <div className={styles.tabs}>
              <div
                className={css({
                  tab: true,
                  selected: selectedTab === tabs.CUSTOMERS,
                })}
                onClick={() => setSelectedTab(tabs.CUSTOMERS)}
              >
                <span>Clientes</span>
              </div>
              <If cond={audience?.version !== "V1"}>
                <div
                  className={css({
                    tab: true,
                    selected: selectedTab === tabs.SEGMENTATION,
                  })}
                  style={{
                    cursor: loadingSegments ? "not-allowed" : "pointer",
                    borderColor:
                      selectedTab === tabs.SEGMENTATION &&
                      loadingSegments &&
                      "transparent",
                    color: loadingSegments && "#8080807d",
                  }}
                  onClick={() =>
                    !loadingSegments && setSelectedTab(tabs.SEGMENTATION)
                  }
                >
                  <span>Segmentação</span>
                  <If cond={loadingSegments}>
                    <div className="ml-10">
                      <div className="spinner" />
                    </div>
                  </If>
                  {getTotalAppliedSegments() > 0 && (
                    <div className={styles.qty}>
                      {getTotalAppliedSegments()}
                    </div>
                  )}
                </div>
              </If>
              <If cond={audience?.version === "V1"}>
                <div
                  className={styles.tab}
                  style={{
                    cursor: "not-allowed",
                    color: "#8080807d",
                  }}
                >
                  <span>Segmentação</span>
                  <If cond={audience?.version === "V1"}>
                    <span className={styles.tooltip}>
                      Este público é de uma versão antiga, portanto esta
                      ferramenta está indisponível.
                    </span>
                  </If>
                </div>
              </If>
            </div>

            {selectedTab === tabs.CUSTOMERS && (
              <div className="d-flex align-center">
                <InputSearch
                  placeholder="Buscar por..."
                  value={search}
                  onChange={(t) => {
                    setSearch(t.target.value);
                    debouncedSearch(t.target.value);
                  }}
                />
                <If cond={audience?.version !== "V1"}>
                  <div className={styles.divisor} />
                  <div
                    className={styles.filters}
                    onClick={() => setOpenOrdering(true)}
                  >
                    <p>Ordenação e limitação</p>
                  </div>
                </If>
              </div>
            )}

            {selectedTab === tabs.SEGMENTATION && (
              <div className="d-flex align-center">
                <InputSearch
                  placeholder="Buscar por..."
                  value={searchSegments}
                  onChange={(e) => setSearchSegments(e.target.value)}
                />
                <div className={styles.divisor} />
                <div
                  className={styles.filters}
                  onClick={() => setOpenFilters(true)}
                >
                  <p>Filtros</p>
                  {selectedFilters.length > 0 && (
                    <span className={styles.quantity}>
                      {selectedFilters.length}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <If cond={loading}>
        <div className={styles.loading}>
          <Loading fullScreen />
        </div>
      </If>

      <If cond={!loading}>
        <div className={styles.fullDatabaseContainer}>
          <div className={styles.scrollContainer} ref={scrollContainerRef}>
            <If cond={selectedTab === tabs.CUSTOMERS}>
              <If cond={customers.length > 0 && !loading}>
                <Customers
                  key="customers-table"
                  loading={loading}
                  customers={customers}
                  audience={audience}
                  brandId={brandId()}
                  unitId={unitId()}
                  hasNext={page_infos.has_next}
                  onLoadMoreData={() => fetchData(true)}
                  onSelectCustomer={(customer_id) => {
                    setSelectedCustomer(customer_id);
                  }}
                />
              </If>
              <If cond={loading && !!page_infos.has_next}>
                <div className={style.loading}>
                  <div className="w-100 d-flex align-center justify-center flex-1 pt-25 pb-25">
                    <Loading />
                  </div>
                </div>
              </If>
              <If cond={customers.length === 0}>
                <If cond={!loading}>
                  <Empty>
                    <i className="fal fa-database" />
                    <p>Nenhum cliente encontrado.</p>
                  </Empty>
                </If>
              </If>
            </If>

            <If cond={selectedTab === tabs.SEGMENTATION}>
              {loadingSegments ? (
                <div className={styles.loading}>
                  <Loading />
                </div>
              ) : (
                <Segmentations
                  key={"segmentations-" + new Date().getTime()}
                  allSegments={allSegments}
                  selected_segments={selectedSegments}
                  appliedSegments={appliedSegments}
                  search={searchSegments}
                  onSelectSegments={(segments) => {
                    const newSegments = { ...segments };

                    const clearOptionsIfModifierIsNever = (data) => {
                      for (const key in data) {
                        if (!data[key]) return;

                        if ((data[key]?.options || [])?.length === 0)
                          delete data[key];

                        if (
                          data[key]?.modifier &&
                          ["never", "has_only"].includes(
                            data[key]?.modifier?.id,
                          ) &&
                          data[key]?.options?.length !== 1
                        ) {
                          data[key].options = (data[key].options || [])?.filter(
                            (option) => {
                              return !Object.values(selectedSegments).some(
                                (segment) =>
                                  segment.options &&
                                  segment.options.some(
                                    (segmentOption) =>
                                      segmentOption.id === option.id,
                                  ),
                              );
                            },
                          );
                        }
                      }
                      return data;
                    };

                    const filteredSegments =
                      clearOptionsIfModifierIsNever(newSegments);

                    setSelectedSegments(filteredSegments);
                  }}
                />
              )}
            </If>
          </div>

          <If cond={audience?.version !== "V1"}>
            <TargetingGuide
              selected_segments={selectedSegments}
              selected_filters={selectedFilters}
              appliedSegments={appliedSegments}
              appliedFilters={appliedFilters}
              readonly={selectedTab == tabs.CUSTOMERS}
              onSegment={() => setSelectedTab(tabs.SEGMENTATION)}
              onExecute={() => {
                setOpenExecute(true);
                execute({
                  brand_id: brandId(),
                  unit_id: unitId(),
                  schema: transformSegmentsIntoQuery(),
                  filters: transformFiltersIntoQuery(),
                  sort: sort_and_limit?.sort,
                  limit: sort_and_limit?.limit,
                  fetch_stats: true,
                });
              }}
              onClear={() => {
                setSelectedSegments([]);
                setSelectedFilters([]);
                setAllSegments(initialSegmentsRef.current);
                setCustomers(initialCustomersRef.current);
                setTotals(initialTotalsRef.current);
              }}
              onClearFilters={() => setSelectedFilters([])}
              onRemoveCondition={(condition) => {
                const countObjectsAndOptions = (data) => {
                  let total = 0;

                  for (const key in data) {
                    if (data[key].options) {
                      total += data[key].options.length;
                    }
                  }

                  return total;
                };

                const removeObjectById = (obj, targetId) => {
                  if (Array.isArray(obj))
                    return obj.filter((item) => item.id !== targetId);

                  return Object.keys(obj).reduce(
                    (acc, key) => {
                      acc[key] =
                        typeof obj[key] === "object" && obj[key] !== null
                          ? removeObjectById(obj[key], targetId)
                          : obj[key];
                      return acc;
                    },
                    Array.isArray(obj) ? [] : {},
                  );
                };

                const totalAppliedSegments =
                  countObjectsAndOptions(selectedSegments);

                if (totalAppliedSegments === 1) return setOpenClear(true);

                setSelectedSegments(
                  removeObjectById(selectedSegments, condition.id),
                );
              }}
              onRemoveFilter={(filter) => {
                if (selectedFilters.length === 1) {
                  setSelectedFilters([]);
                  setAllSegments(initialSegmentsRef.current);
                  setCustomers(initialCustomersRef.current);
                  setTotals(initialTotalsRef.current);
                  return;
                }

                const filteredArray = selectedFilters.filter(
                  (item) => item.type !== filter.type,
                );

                setSelectedFilters(filteredArray);
              }}
            />
          </If>

          {openExecute && (
            <ProgressModal onCancelExecution={() => setOpenExecute(false)} />
          )}
        </div>
      </If>

      <CustomerDrawer
        visible={!!selected_customer}
        customerId={selected_customer?.id}
        brandId={brandId()}
        unitId={unitId()}
        onClose={() => setSelectedCustomer(null)}
      />

      <Filters
        visible={openFilters}
        readyonly={selectedTab == tabs.CUSTOMERS}
        onClose={() => setOpenFilters(false)}
        onApplyFilters={(filters) => {
          setOpenFilters(false);
          setSelectedFilters(filters);
        }}
        clearFilters={() => {
          // setOpenFilters(false);
          setSelectedFilters([]);
        }}
        selected_filters={selectedFilters}
        extra_variables={customerAttributeVariables}
      />

      <OrderingAndLimitation
        visible={openOrdering}
        onSave={(s) => {
          setSortAndLimit(s);
          setOpenOrdering(false);
          setOpenExecute(true);
          execute({
            brand_id: brandId(),
            unit_id: unitId(),
            schema: transformSegmentsIntoQuery(),
            sort: s?.sort,
            limit: s?.limit,
            fetch_stats: true,
          });
        }}
        onClose={() => setOpenOrdering(false)}
      />

      <SaveAudience
        version={"v2"}
        visible={openSave}
        audience={audience}
        onClose={() => setOpenSave(false)}
        state={{
          brand_id: brandId(),
          unit_id: unitId(),
          schema: Object.entries(appliedSegments).map(([key, value]) => ({
            key,
            modifier: value.modifier?.id || "has_some",
            values: (value.options || []).map((v) => v.id),
          })),
          boxes: appliedSegments,
          links: [],
          filters: transformFiltersIntoQuery(),
          sort: page_infos?.sort,
          limit: page_infos?.limit,
        }}
      />
      <ExportAudience
        version={"v2"}
        visible={openExport}
        execution_id={page_infos?.execution_id}
        audience={audience}
        onClose={() => setOpenExport(false)}
      />

      <Drawer
        visible={openClear}
        onClose={() => setOpenClear(false)}
        direction="center"
        title="Atenção"
        showCloseButton={false}
      >
        <div className={styles.clear}>
          <p className={styles.message}>
            Você tem certeza que deseja limpar toda a triagem de públicos?
          </p>

          <div className={styles.actions}>
            <Button
              design="primary"
              onClick={() => {
                setSelectedSegments([]);
                setAppliedSegments([]);

                setSelectedFilters([]);
                setAppliedFilters([]);

                setSortAndLimit(null);

                setOpenClear(false);

                setOpenExecute(true);
                execute({
                  brand_id: brandId(),
                  unit_id: unitId(),
                  schema: [],
                  sort: sort_and_limit?.sort,
                  limit: sort_and_limit?.limit,
                  fetch_stats: true,
                });
              }}
              className="w-100"
            >
              Limpar tudo
            </Button>
            <Button className="w-100" onClick={() => setOpenClear(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Drawer>

      <AudienceFilters
        visible={openAudienceFilters}
        boxes={audience?.boxes}
        links={audience?.links}
        audience={audience}
        totals={totals}
        onClose={() => setOpenAudienceFilters(false)}
      />

      <CustomerMarkupsDrawer
        open={openMarkups}
        onClose={() => setOpenMarkups(false)}
        alreadyDrawer={false}
        isSegments
        execution_id={page_infos?.execution_id}
      />
    </>
  );
}

const waitQueryExecution = async (schema, options) => {
  const { execution_id } = await ocdp_api.query(schema, options);
  if (!execution_id) {
    return null;
  }

  if (options?.version === "V1")
    return await Oraculo.waitQueryExecution(execution_id);

  return await Cdp.waitQueryExecution(execution_id);
};
